import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { ActionIcon, Box, Drawer, NavLink, Stack, useMantineColorScheme, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useState } from "react";
import { CV_SECTION_COLORS } from "../../../constants/cvSectionColors";
import { CV_SECTIONS } from "../constants/cvSections";

interface CVSidebarProps {
    activeSection: string;
    onSectionClick: (sectionId: string) => void;
    children?: React.ReactNode;
    sections?: typeof CV_SECTIONS;
}

export function CVSidebar({
    activeSection,
    onSectionClick,
    children,
    sections = CV_SECTIONS,
}: CVSidebarProps) {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === "dark";
    const isMobile = useMediaQuery("(max-width: 768px)");
    const [drawerOpen, setDrawerOpen] = useState(false);

    const handleSectionClick = (sectionId: string) => {
        onSectionClick(sectionId);
        setDrawerOpen(false);
    };

    const navContent = (
        <Stack gap={2}>
            {sections.map((section) => {
                const sectionColor = CV_SECTION_COLORS[section.id];
                if (!sectionColor && section.id !== "personal-details") return null;

                const color = sectionColor ? sectionColor.color : "blue";
                const isActive = activeSection === section.id;

                return (
                    <NavLink
                        key={section.id}
                        label={section.label}
                        active={isActive}
                        onClick={() => handleSectionClick(section.id)}
                        style={{
                            borderRadius: 4,
                            whiteSpace: "nowrap",
                            padding: "8px 12px",
                            fontSize: "0.9rem",
                            borderLeft: `4px solid var(--mantine-color-${color}-6)`,
                            backgroundColor: isActive
                                ? `var(--mantine-color-${color}-0)`
                                : "transparent",
                        }}
                        styles={{
                            root: {
                                "&": {
                                    backgroundColor: isActive
                                        ? `var(--mantine-color-${color}-0)`
                                        : "transparent",
                                },
                                "&[dataMantineActive]": {
                                    backgroundColor: `var(--mantine-color-${color}-0)`,
                                    fontWeight: 600,
                                },
                                "&:hover": {
                                    backgroundColor: isActive
                                        ? `var(--mantine-color-${color}-0)`
                                        : "var(--mantine-color-gray-0)",
                                },
                            },
                        }}
                        variant="subtle"
                    />
                );
            })}
        </Stack>
    );

    if (isMobile) {
        return (
            <>
                {/* Fixed top bar with hamburger — no layout width, sits above content */}
                <Box
                    style={{
                        position: "fixed",
                        top: 60, // below AppShell header
                        left: 0,
                        right: 0,
                        zIndex: 100,
                        background: isDark ? "var(--mantine-color-dark-7)" : "white",
                        borderBottom: `1px solid ${isDark ? "var(--mantine-color-dark-4)" : theme.colors.gray[2]}`,
                        padding: "6px 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                    }}
                >
                    <ActionIcon
                        color="brand"
                        variant="subtle"
                        size="md"
                        onClick={() => setDrawerOpen(true)}
                        aria-label="Open navigation"
                    >
                        <Bars3Icon width={20} />
                    </ActionIcon>
                    <Box style={{ fontSize: "0.85rem", color: isDark ? "var(--mantine-color-dark-1)" : theme.colors.gray[6], fontWeight: 500 }}>
                        {sections.find(s => s.id === activeSection)?.label ?? ""}
                    </Box>
                </Box>

                <Drawer
                    opened={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    position="left"
                    size={240}
                    padding="xs"
                    withCloseButton={false}
                    styles={{
                        body: { padding: 8 },
                        content: {
                            background: isDark ? "var(--mantine-color-dark-7)" : "white",
                        },
                    }}
                >
                    <Box style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={() => setDrawerOpen(false)}
                            aria-label="Close navigation"
                        >
                            <XMarkIcon width={18} />
                        </ActionIcon>
                    </Box>
                    {children && <Box mb="md">{children}</Box>}
                    {navContent}
                </Drawer>
            </>
        );
    }

    return (
        <Box
            style={{
                borderRight: `1px solid ${theme.colors.gray[3]}`,
                padding: theme.spacing.xs,
                width: "220px",
                flexShrink: 0,
                position: "sticky",
                top: 0,
                alignSelf: "flex-start",
                height: "calc(100vh - 92px)",
                overflowY: "auto",
                zIndex: 10,
            }}
        >
            {children && <Box mb="md">{children}</Box>}
            {navContent}
        </Box>
    );
}
